import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, MonitorSmartphone, X } from 'lucide-react';
import { v4 } from 'uuid';
import { useAlertContext } from '../contexts/Alert';
import { USER_URL } from '../api_base.js';
import { buildHeaders } from '../utils/apiHeaders.js';
import { checkMaintenance } from '../utils/checkMaintenance.js';

const Devices = () => {
    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState([]);

    const { setAlerts } = useAlertContext();
    const navigate = useNavigate();

    const pushAlert = (text) => setAlerts(prev => ([...prev, { id: v4(), text }]));

    const fetchDevices = async () => {
        try {
            const res = await fetch(USER_URL + "/devices", {
                method: "GET",
                credentials: "include",
                headers: buildHeaders(),
            });
            const data = await res.json();

            if (!res.ok || data.failed) {
                if (data.signout) navigate("/auth");
                pushAlert(data.message || "Oops! something went wrong while loading your devices.");
                return;
            }

            setDevices(data.data || []);
            checkMaintenance(data , navigate)
        } catch (err) {
            pushAlert("Oops! something went wrong while reaching the server.");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { fetchDevices(); }, []);

    const revokeDevice = async (deviceId) => {
        setBusy(prev => ([...prev, deviceId]));
        try {
            const res = await fetch(USER_URL + "/devices/" + deviceId, {
                method: "DELETE",
                credentials: "include",
                headers: buildHeaders(),
            });
            const data = await res.json();

            if (!res.ok || data.failed) {
                pushAlert(data.message || "Oops! something went wrong while revoking this device.");
                return;
            }

            setDevices(prev => prev.filter(d => d.deviceId !== deviceId));
        } catch (err) {
            pushAlert("Oops! something went wrong while reaching the server.");
        } finally {
            setBusy(prev => prev.filter(id => id !== deviceId));
        }
    }

    const revokeGroup = async (type) => {
        setBusy(prev => ([...prev, type]));
        try {
            const res = await fetch(USER_URL + "/devices?revoke=" + type, {
                method: "DELETE",
                credentials: "include",
                headers: buildHeaders(),
            });
            const data = await res.json();

            if (!res.ok || data.failed) {
                pushAlert(data.message || "Oops! something went wrong while revoking devices.");
                return;
            }

            pushAlert(data.message || "Devices revoked successfully.");
            await fetchDevices();
        } catch (err) {
            pushAlert("Oops! something went wrong while reaching the server.");
        } finally {
            setBusy(prev => prev.filter(id => id !== type));
        }
    }

    return (
        <div className="page">
            <div className="page_header">
                <button className="back" onClick={() => navigate("/")}><ChevronLeft /></button>
                <div className="page_title">
                    <h2>Devices</h2>
                    <p>Manage where you're signed in.</p>
                </div>
            </div>

            <div className="page_body">
                <div className="devices_actions">
                    <button className="pill_button ghost" disabled={busy.includes("other")} onClick={() => revokeGroup("other")}>
                        {busy.includes("other") ? <span className="loading"></span> : "Sign out other devices"}
                    </button>
                    <button className="pill_button danger" disabled={busy.includes("all")} onClick={() => revokeGroup("all")}>
                        {busy.includes("all") ? <span className="loading"></span> : "Sign out all devices"}
                    </button>
                </div>

                {loading ? (
                    <div className="empty_state"><span className="loading"></span></div>
                ) : devices.length === 0 ? (
                    <div className="empty_state">
                        <MonitorSmartphone size="40" />
                        <h3>No active devices</h3>
                        <p>You're not signed in anywhere else right now.</p>
                    </div>
                ) : (
                    <div className="devices_list">
                        {devices.map(device => (
                            <div className="device_row" key={device.deviceId}>
                                <div className="device_icon"><MonitorSmartphone size="18" /></div>
                                <div className="device_meta">
                                    <h4>{device.deviceInfo?.platform || "Unknown device"}</h4>
                                    <span>{device.location?.city ? `${device.location.city}, ${device.location.country}` : "Unknown location"}</span>
                                </div>
                                <button className="revoke" disabled={busy.includes(device.deviceId)} onClick={() => revokeDevice(device.deviceId)}>
                                    {busy.includes(device.deviceId) ? <span className="loading"></span> : <X size="18" />}
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}

export default Devices;
